import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { components, type OptionProps } from '@atlaskit/select';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { GREY_META } from '@atlassian/jira-option-color-picker/src/color-picker/constants.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { LabelOption } from '../model/index.tsx';
import messages from './messages.tsx';

export const ColoredLabel = ({
	optionProps,
	createNewLabelString,
	onEditOption,
	inputRef,
	lastFocusedKey,
	isFocusingOptions,
	isLoading,
	isProjectAdmin,
	isGlobalAdmin,
}: {
	optionProps: OptionProps<LabelOption, boolean>;
	createNewLabelString: string;
	onEditOption: (option: LabelOption) => void;
	inputRef: React.RefObject<HTMLInputElement>;
	lastFocusedKey?: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
	isFocusingOptions?: boolean;
	isLoading?: boolean;
	isProjectAdmin?: boolean;
	isGlobalAdmin?: boolean;
}) => {
	const [isOptionFocused, setIsOptionFocused] = useState(false);
	const [isOptionHovered, setIsOptionHovered] = useState(false);

	return (
		<components.Option {...optionProps} isFocused={isOptionFocused || isOptionHovered}>
			<RenderLabel
				option={optionProps.data}
				createNewLabelString={createNewLabelString}
				onEditOption={onEditOption}
				inputRef={inputRef}
				lastFocusedKey={lastFocusedKey}
				isFocusingOptions={isFocusingOptions}
				isLoading={isLoading}
				isProjectAdmin={isProjectAdmin}
				isGlobalAdmin={isGlobalAdmin}
				isOptionFocused={isOptionFocused}
				isOptionHovered={isOptionHovered}
				setIsOptionFocused={setIsOptionFocused}
				setIsOptionHovered={setIsOptionHovered}
			/>
		</components.Option>
	);
};

export const RenderLabel = ({
	option,
	createNewLabelString,
	onEditOption,
	inputRef,
	lastFocusedKey,
	isFocusingOptions,
	isLoading,
	isProjectAdmin,
	isGlobalAdmin,
	isOptionFocused,
	isOptionHovered,
	setIsOptionFocused,
	setIsOptionHovered,
}: {
	option: LabelOption;
	createNewLabelString: string;
	onEditOption: (option: LabelOption) => void;
	inputRef: React.RefObject<HTMLInputElement>;
	lastFocusedKey?: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
	isFocusingOptions?: boolean;
	isLoading?: boolean;
	isProjectAdmin?: boolean;
	isGlobalAdmin?: boolean;
	isOptionFocused?: boolean;
	isOptionHovered?: boolean;
	setIsOptionFocused?: (input: boolean) => void;
	setIsOptionHovered?: (input: boolean) => void;
}) => {
	const optionLabel: string = option.label;

	const pressableRefsNew = useRef<Record<string, HTMLButtonElement[]>>({});

	useLayoutEffect(() => {
		if (!lastFocusedKey || !lastFocusedKey?.current) {
			return;
		}

		const { key, index, type } = lastFocusedKey?.current;

		if (isFocusingOptions && !isLoading) {
			if (pressableRefsNew?.current[key]?.[index] && type === 'button') {
				requestAnimationFrame(() => {
					pressableRefsNew?.current[key]?.[index]?.focus();
				});
			}
			if (type === 'input' && inputRef.current) {
				requestAnimationFrame(() => {
					inputRef?.current?.focus();
				});
			}
		}
	}, [inputRef, isFocusingOptions, isLoading, lastFocusedKey, pressableRefsNew]);

	const handleMouseOver = () => {
		setIsOptionFocused?.(false);
		setIsOptionHovered?.(true);
	};

	const handleMouseOut = () => {
		setIsOptionHovered?.(false);
	};

	const handleFocus = () => {
		setIsOptionHovered?.(false);
		setIsOptionFocused?.(true);
	};

	const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
		const relatedTarget = e.relatedTarget;
		if (!e.currentTarget.contains(relatedTarget)) {
			setIsOptionFocused?.(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.stopPropagation();

			requestAnimationFrame(() => {
				inputRef?.current?.focus();
			});
		}
	};

	const handleButtonFocus = () => {
		const focusedKey = lastFocusedKey;
		if (focusedKey) {
			// eslint-disable-next-line react-hooks/react-compiler
			focusedKey.current = { key: optionLabel, index: 0, type: 'button' };
		}
	};

	const handleRef = (el: HTMLButtonElement | null) => {
		if (!el) return;

		// Initialize array if not already present
		if (!pressableRefsNew?.current[optionLabel]) {
			pressableRefsNew.current[optionLabel] = [];
		}

		// Avoid duplicate refs
		if (!pressableRefsNew.current[optionLabel].includes(el)) {
			pressableRefsNew.current[optionLabel].push(el);
		}
	};

	if (optionLabel.endsWith(createNewLabelString)) {
		return (
			<Box
				xcss={optionContentStyles}
				role="group"
				tabIndex={-1}
				onMouseOver={handleMouseOver}
				onMouseOut={handleMouseOut}
				onFocus={handleFocus}
				onBlur={handleBlur}
			>
				<Pressable
					xcss={pressableStyles}
					backgroundColor="color.background.neutral.subtle"
					onKeyDown={handleKeyDown}
					onFocus={handleButtonFocus}
					ref={handleRef}
				>
					<OptionTag name={optionLabel} color={GREY_META.name} />
				</Pressable>
			</Box>
		);
	}

	return (
		<Box
			xcss={optionContentStyles}
			role="group"
			tabIndex={-1}
			onMouseOver={handleMouseOver}
			onMouseOut={handleMouseOut}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<Pressable
				xcss={pressableStyles}
				backgroundColor="color.background.neutral.subtle"
				onKeyDown={handleKeyDown}
				onFocus={handleButtonFocus}
				ref={handleRef}
			>
				<OptionTag
					name={optionLabel}
					color={option.color ?? memoizeGetAutoGeneratedColorForLabels(optionLabel)}
					isColoredLabel
				/>
			</Pressable>
			<RenderInlineEditButton
				option={option}
				onEditOption={onEditOption}
				lastFocusedKey={lastFocusedKey}
				pressableRefsNew={pressableRefsNew}
				isProjectAdmin={isProjectAdmin}
				isGlobalAdmin={isGlobalAdmin}
				isFocused={isOptionFocused || isOptionHovered}
			/>
		</Box>
	);
};

const RenderInlineEditButton = ({
	option,
	onEditOption,
	lastFocusedKey,
	pressableRefsNew,
	isProjectAdmin,
	isGlobalAdmin,
	isFocused,
}: {
	option: LabelOption;
	onEditOption: (option: LabelOption) => void;
	lastFocusedKey?: React.MutableRefObject<{ key: string; index: number; type: string } | null>;
	pressableRefsNew: React.MutableRefObject<Record<string, HTMLButtonElement[]>>;
	isProjectAdmin?: boolean;
	isGlobalAdmin?: boolean;
	isFocused?: boolean;
}) => {
	const { formatMessage } = useIntl();

	const handleOnClickEditButton = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		onEditOption?.(option);
	};

	const handleRef = (el: HTMLButtonElement | null) => {
		if (!el) return;

		// Avoid duplicate refs
		if (!pressableRefsNew?.current[option.label].includes(el)) {
			pressableRefsNew?.current[option.label].push(el);
		}
	};

	const handleFocus = () => {
		const focusedKeyRef = lastFocusedKey;
		if (focusedKeyRef) {
			// eslint-disable-next-line react-hooks/react-compiler
			focusedKeyRef.current = { key: option.label, index: 1, type: 'button' };
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.stopPropagation();
		}
	};

	if ((isProjectAdmin || isGlobalAdmin) && option.label && isFocused)
		return (
			<IconButton
				ref={handleRef}
				onFocus={handleFocus}
				testId={`jira-label-picker.inline-edit-button-${option.label}`}
				appearance="subtle"
				icon={EditIcon}
				spacing="compact"
				onClick={handleOnClickEditButton}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				label={formatMessage(messages.editOption, { optionName: option.label })}
			/>
		);

	return null;
};

const optionContentStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	justifyContent: 'space-between',
});
const pressableStyles = xcss({ height: '24px' });
